import * as React from 'react';
import { useCallback, useContext, useMemo } from 'react';
import cn from 'classnames';
import { SelectedUpgrade } from '../../IncludedBaggage/types';
import { PassengerLabels } from '../../../Checkout/types';
import { Baggage } from '../../../BaggageService/types';
import { createStore } from '../store';
import { Provider, useSelector } from 'react-redux';
import { UpgradeBaggageHeaderProps } from '../types';
import BaggageDialogHeader from './BaggageDialogHeader/BaggageDialogHeader';
import BaggageBody from './BaggageBody/BaggageBody';
import ModalX from '../../../BaseComponents/ModalX';
import { ModalXProps } from '../../../BaseComponents/ModalX';
import { MobileDialog } from '@shared/ui/MobileDialog';
import { EngineTheme, useTheme } from '../../../theme';
import BaggageFooter from './BaggageFooter/BaggageFooter';
import BaggageMobileFooter from './BaggageMobileFooter/BaggageMobileFooter';
import { getBaggageHasChanges, getSelectedServicesWithConfirmedCount, joinServicesByRfics } from '../utils';
import { BaggageType } from '@websky/graphql';
import { saveServices, setAllServices } from '../store/actions';
import { useRenderers } from '../../../renderProps';
import { Passenger, Segment } from '../../types';
import SimpleLoader from '../../../SimpleLoader';
import WebskyAnalyticsApollo from '../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { WxEvents } from '../../../DataLayerAnalytics/types';
import { ModeContext, useConfig } from '../../../context';
import { Mode } from '../../../types';
import { ServiceMeta } from '../../../Checkout/store/selectedServices/actions';
import { getUpgradableBaggage as getCheckinUpgradableBaggage } from '../../../Modules/Checkin/store/order/selectors';
import { getUpgradableBaggage as getCheckoutUpgradableBaggage } from '../../../Checkout/store/order/selectors';
import { useGdsServicesCalculatedCountInfo } from '@shared/lib/gdsService/useGdsServicesCalculatedCountInfo';
import { useIsMobile } from '../../../utils';

interface Props {
	open: boolean;
	onClose: () => void;
	passengers: Passenger[];
	passengerLabels: PassengerLabels;
	segments: Segment[];
	includedBaggage?: Baggage[];
	upgradeBaggage: Baggage[];
	disabled: boolean;
	selectedServices: SelectedUpgrade[]; // все выбранные услуги с типом Baggage
	headerProps: UpgradeBaggageHeaderProps;
	baggageType?: BaggageType;
	dynamicBaggagePricing?: boolean; // запрос на сохранение при каждом изменении услуг
	isLoading?: boolean;
	className?: string;
	analyticsMeta?: ServiceMeta;
	valueAlign?: 'center' | 'right';
	dataTestId?: string;
}

const UpgradeBaggageDialog: React.FC<Props> = props => {
	const theme = useTheme('UpgradeBaggageDialog').UpgradeBaggageDialog;
	const isMobile = useIsMobile();

	const config = useConfig();

	const [mobileServiceRfics, setMobileServiceRfics] = React.useState(props.upgradeBaggage[0].rfics);

	const baggagesByRfics = React.useMemo(() => joinServicesByRfics(props.upgradeBaggage), [props.upgradeBaggage]);

	const mode = useContext(ModeContext);

	let upgradableBaggage: Baggage[];

	if (mode === Mode.Checkin) {
		upgradableBaggage = useSelector(getCheckinUpgradableBaggage);
	} else {
		upgradableBaggage = useSelector(getCheckoutUpgradableBaggage(config.global.servicesAvailable.animal));
	}

	const gdsServicesCalculatedLimitInfo = useGdsServicesCalculatedCountInfo();

	const currentBaggageTabRef = React.useRef<string | null>(null);

	const firstBaggageTabRfics =
		props.baggageType === BaggageType.SportingEquipment ? baggagesByRfics[0]?.[0]?.rfics : undefined;

	const store = React.useMemo(() => {
		return createStore(
			props.upgradeBaggage,
			props.segments,
			props.passengers,
			getSelectedServicesWithConfirmedCount(props.selectedServices, props.passengers),
			firstBaggageTabRfics ? currentBaggageTabRef.current ?? firstBaggageTabRfics : null,
			config,
			firstBaggageTabRfics,
			props.disabled,
			upgradableBaggage,
			gdsServicesCalculatedLimitInfo
		);
	}, [
		props.upgradeBaggage,
		props.segments,
		props.passengers,
		props.selectedServices,
		props.open,
		props.disabled,
		upgradableBaggage,
		gdsServicesCalculatedLimitInfo,
		config
	]);

	const initialState = React.useMemo(() => store.getState(), [store]);

	React.useEffect(() => {
		if (props.open) {
			store.dispatch(
				setAllServices(getSelectedServicesWithConfirmedCount(props.selectedServices, props.passengers))
			);

			if (baggagesByRfics.length === 1) {
				const baggage = baggagesByRfics[0].find(baggage => baggage.canBeAdded) ?? baggagesByRfics[0][0];

				WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.BaggageItem, {
					servicesIds: [baggage.id],
					segmentId: baggage.segmentIds[0][0]
				});
			}
		}
	}, [props.open, props.selectedServices, props.passengers, store]);

	React.useEffect(() => {
		if (!props.open) {
			currentBaggageTabRef.current = null;

			return null;
		}
		currentBaggageTabRef.current = store.getState().baggageTab;
		const unsubscribe = store.subscribe(() => {
			currentBaggageTabRef.current = store.getState().baggageTab;
		});

		return unsubscribe;
	}, [props.open, store]);

	const handlerConfirm = useCallback(() => {
		const actualState = store.getState();
		const hasChanges = getBaggageHasChanges(initialState.selectedServices, actualState.selectedServices);

		if (hasChanges) {
			store.dispatch(saveServices({ meta: props.analyticsMeta }));
		}

		props.onClose();
	}, [store, props.analyticsMeta, JSON.stringify(initialState.selectedServices)]);

	const {
		specialEquipmentItemsSort,
		BaggageDialogHeader: BaggageDialogHeaderOverride = BaggageDialogHeader,
		renderBaggageBody: RenderBaggageBodyOverride = BaggageBody
	} = useRenderers();

	const sortedBaggagesByRfics = specialEquipmentItemsSort
		? baggagesByRfics.sort(specialEquipmentItemsSort)
		: baggagesByRfics.sort((first, second) => first[0].price.amount - second[0].price.amount);

	const footerProps = {
		onCancel: props.onClose,
		onConfirm: handlerConfirm,
		initialSelectedServices: initialState.selectedServices
	};

	const content = (
		<div className={theme.root}>
			{props.isLoading && <SimpleLoader />}

			<BaggageDialogHeaderOverride
				services={sortedBaggagesByRfics}
				headerProps={props.headerProps}
				segments={props.segments}
				setMobileServiceId={setMobileServiceRfics}
				mobileSelectedServiceId={mobileServiceRfics}
			/>

			<RenderBaggageBodyOverride
				upgradeBaggages={baggagesByRfics}
				passengers={props.passengers}
				passengerLabels={props.passengerLabels}
				segments={props.segments}
				mobileSelectedServiceId={mobileServiceRfics}
				valueAlign={props.valueAlign || 'center'}
			/>
		</div>
	);

	const mobileDialogClasses = useMemo<EngineTheme['Shared']['MobileDialog']>(
		() => ({
			paper: theme.mobile__paper,
			root: theme.modal__root,
			content: theme.mobileDialog__content
		}),
		[]
	);

	return (
		<Provider store={store}>
			{isMobile ? (
				<MobileDialog
					classes={mobileDialogClasses}
					title={props.headerProps.header}
					isOpen={props.open}
					onClose={props.onClose}
					footer={<BaggageMobileFooter {...footerProps} />}
				>
					{content}
				</MobileDialog>
			) : (
				<ModalX
					isOpen={props.open}
					onClose={props.onClose}
					size="large"
					noPadding
					scroll="body"
					classes={{
						root: theme.modal__root,
						paper: cn(theme.modal__paper, props.className),
						top_wrapper: theme.top_wrapper
					}}
					title={<div className={theme.modalHeader}>{props.headerProps.header}</div>}
					paperProps={{ 'data-test-id': props.dataTestId } as ModalXProps['paperProps']}
					footer={<BaggageFooter {...footerProps} />}
				>
					{content}
				</ModalX>
			)}
		</Provider>
	);
};

export default UpgradeBaggageDialog;
